/**
 * R-131-ATTACH — bug-report attachment upload service.
 *
 * Mirrors the logo upload flow in `reportThemeService.ts` (same-origin POST to
 * `/api/storage/upload-file`, base64 body, abort-timeout), but writes to a
 * PER-USER `users/{uid}/bug-attachments/{id}` prefix and returns the
 * token-gated media URL the server hands back. That URL is readable by an
 * admin from the triage panel (the download token is the credential), unlike
 * the auth-gated `/api/storage/download-file` proxy which only the owner can
 * reach.
 *
 * Validation here is client-side UX (fail fast with a clear message); the
 * server re-validates size + content type, and `createBugReport` re-validates
 * that every stored URL resolves under the caller's own prefix.
 */
import { authedFetch, createFetchAbortTimeout } from '../lib/authedFetch';
import {
  BUG_ATTACHMENT_ALLOWED_TYPES,
  BUG_ATTACHMENT_MAX_BYTES,
  type BugAttachment,
} from '../types/bugReport';
import type { UploadProgress } from './reportThemeService';

const ATTACHMENT_UPLOAD_FETCH_TIMEOUT_MS = 120_000;

/** Lower-cased extension (no dot) or '' when none. */
function fileExt(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0) return '';
  return name.slice(dot + 1).toLowerCase();
}

/** Best-effort canonical content type for an attachment file. */
export function resolveAttachmentContentType(file: File): string {
  const type = (file.type || '').toLowerCase();
  if (BUG_ATTACHMENT_ALLOWED_TYPES.has(type)) {
    return type === 'image/jpg' ? 'image/jpeg' : type;
  }
  const ext = fileExt(file.name);
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'pdf') return 'application/pdf';
  return type || 'application/octet-stream';
}

/** True when the file is an allowed attachment (image or PDF) by MIME or extension. */
export function isAllowedAttachmentFile(file: File): boolean {
  const ct = resolveAttachmentContentType(file);
  return BUG_ATTACHMENT_ALLOWED_TYPES.has(ct);
}

function extForContentType(contentType: string, fallback: string): string {
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType === 'image/jpeg') return 'jpg';
  const sub = contentType.split('/')[1];
  return (sub ? sub.replace(/[^a-z0-9]/gi, '').toLowerCase() : '') || fallback || 'bin';
}

/** Read a File as raw base64 (no data: prefix). Node-safe fallback for tests. */
async function fileToBase64(file: File): Promise<string> {
  if (typeof FileReader === 'undefined') {
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
    return btoa(binary);
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read attachment file'));
        return;
      }
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read attachment file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a single bug attachment and resolve to a {@link BugAttachment} ref.
 *
 * Throws (with a user-facing message) on disallowed type, oversize, or a
 * server error. Callers should surface the message and let the user retry /
 * remove the file.
 */
export async function uploadBugAttachment(
  file: File,
  uid: string,
  onProgress?: (p: UploadProgress) => void,
): Promise<BugAttachment> {
  if (!uid) throw new Error('Sign in to attach files.');
  if (!isAllowedAttachmentFile(file)) {
    throw new Error(`File type "${file.type || 'unknown'}" not allowed. Use an image (PNG/JPEG/WebP/GIF) or PDF.`);
  }
  if (file.size > BUG_ATTACHMENT_MAX_BYTES) {
    throw new Error(`"${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Max 10 MB.`);
  }

  const contentType = resolveAttachmentContentType(file);
  const ext = extForContentType(contentType, fileExt(file.name));
  const attachmentId = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}.${ext}`;
  const storagePath = `users/${uid}/bug-attachments/${attachmentId}`;

  onProgress?.({ bytesTransferred: 0, totalBytes: file.size, progress: 0 });
  const content = await fileToBase64(file);
  onProgress?.({ bytesTransferred: Math.floor(file.size * 0.5), totalBytes: file.size, progress: 0.5 });

  const { signal, cancel } = createFetchAbortTimeout(ATTACHMENT_UPLOAD_FETCH_TIMEOUT_MS);
  let res: Response;
  try {
    res = await authedFetch('/api/storage/upload-file', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: storagePath, content, contentType }),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Attachment upload timed out — check your connection and try again.');
    }
    if (err instanceof Error && /abort/i.test(err.message) && signal.aborted) {
      throw new Error('Attachment upload timed out — check your connection and try again.');
    }
    throw err;
  } finally {
    cancel();
  }

  const json = (await res.json().catch(() => null)) as
    | { downloadUrl?: string; error?: string; detail?: string }
    | null;
  if (!res.ok) {
    const msg = json?.error || json?.detail || `Attachment upload failed (${res.status})`;
    throw new Error(typeof msg === 'string' ? msg : 'Attachment upload failed');
  }
  if (!json?.downloadUrl) {
    throw new Error('Attachment upload did not return a URL — please retry.');
  }

  onProgress?.({ bytesTransferred: file.size, totalBytes: file.size, progress: 1 });
  return {
    url: json.downloadUrl,
    name: file.name.slice(0, 200),
    contentType,
    size: file.size,
  };
}
